import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getIdToken,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  console.log('🔐 AuthProvider component rendering...');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  console.log('🔐 AuthProvider initial state - loading:', loading, 'user:', user);

  useEffect(() => {
    console.log('🔍 Setting up auth state listener...');
    let authResolved = false;

    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(
      auth, 
      (user) => {
        console.log('🔄 Auth state changed:', user ? `User: ${user.email}` : 'No user');
        authResolved = true;
        setUser(user);
        setLoading(false);
        
        // Store token in localStorage for API calls
        if (user) {
          user.getIdToken().then((token) => {
            localStorage.setItem('firebaseToken', token);
            console.log('✅ Token stored for user:', user.email);
          });
        } else {
          localStorage.removeItem('firebaseToken');
        }
      }, 
      (error) => {
        // Handle Firebase initialization errors gracefully
        console.warn('⚠️ Firebase auth error (continuing anyway):', error);
        authResolved = true;
        setUser(null);
        setLoading(false);
      }
    );

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (!authResolved) {
        console.warn('⚠️ Firebase auth timeout after 3 seconds - forcing loading to false');
        setLoading(false);
      }
    }, 3000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      console.log('🔵 Starting Google sign-in popup...');
      const result = await signInWithPopup(auth, googleProvider);
      console.log('✅ Sign-in popup completed');
      
      const token = await getIdToken(result.user);
      localStorage.setItem('firebaseToken', token);
      console.log('✅ Firebase sign-in successful, user:', result.user.email);
      console.log('✅ Token stored, auth state will update automatically');
    } catch (error: any) {
      console.error('❌ Error signing in with Google:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      localStorage.removeItem('firebaseToken');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const getToken = async (): Promise<string | null> => {
    if (!user) return null;
    try {
      const token = await getIdToken(user, true); // Force refresh
      localStorage.setItem('firebaseToken', token);
      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signOut,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
